import { MAX_EMAIL_CHARS } from "../names/credentials.ts";
import { isMailToken } from "./links.ts";

/**
 * The request the gateway posts to the mail Worker (ADR-092 decision 2). It carries no words and no link: which
 * of the fixed mails to send, to whom, and the token its link carries. The Worker builds the link from its own
 * origin and renders the words itself, so whoever holds the signing secret can cause only those mails. The
 * reader is the Worker's whole trust in a body: any other shape, any unknown field and any recipient that is
 * not one address is refused.
 */

/**
 * The request's version, its `v`.
 */
export const MAIL_REQUEST_VERSION = 1;

/**
 * A request: `verify` and `reset` carry a recipient and a token; `changed` a recipient and no token; `ping`,
 * which the Worker answers without sending anything, neither.
 */
export type MailRequest =
  | { readonly v: 1; readonly id: string; readonly kind: "verify" | "reset"; readonly to: string; readonly token: string }
  | { readonly v: 1; readonly id: string; readonly kind: "changed"; readonly to: string }
  | { readonly v: 1; readonly id: string; readonly kind: "ping" };

/**
 * The kinds a request may name.
 */
export type MailRequestKind = MailRequest["kind"];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const ADDRESS = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;
const CONTROL = /[\u0000-\u001f\u007f-\u009f]|\p{Zl}|\p{Zp}/u;
const FIELDS: Readonly<Record<MailRequestKind, readonly string[]>> = {
  verify: ["v", "id", "kind", "to", "token"],
  reset: ["v", "id", "kind", "to", "token"],
  changed: ["v", "id", "kind", "to"],
  ping: ["v", "id", "kind"],
};

/**
 * Whether a value is one address a mail may go to: a local part, one at sign and a dotted domain, at most 254
 * characters, with no white space, line break, control character, angle bracket, comma or semicolon, so it can
 * never read as two recipients or as a header.
 *
 * @param value The value.
 * @returns True for one address.
 */
export function isMailRecipient(value: unknown): value is string {
  return typeof value === "string" && value.length <= MAX_EMAIL_CHARS && ADDRESS.test(value) && !CONTROL.test(value);
}

/**
 * Whether a value is one of the kinds, read from the table's own keys alone.
 *
 * @param value The value.
 * @returns True for a kind.
 */
function isKind(value: unknown): value is MailRequestKind {
  return typeof value === "string" && Object.hasOwn(FIELDS, value);
}

/**
 * Reads a parsed request body.
 *
 * @param value The parsed JSON.
 * @returns The request, or null when it is not an object of exactly its kind's fields: an unknown field, kind
 *   or version, an id that is not a lower-case UUID, a recipient that is not one address, a token that is not 43
 *   base64url characters, a token on `changed` or `ping`, or a recipient on `ping`.
 */
export function readMailRequest(value: unknown): MailRequest | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const body = value as Record<string, unknown>;
  const { v, id, kind, to, token } = body;
  if (v !== MAIL_REQUEST_VERSION || typeof id !== "string" || id.length !== 36 || !UUID.test(id) || !isKind(kind)) {
    return null;
  }
  const fields = FIELDS[kind];
  const keys = Object.keys(body);
  if (keys.length !== fields.length || !keys.every((key) => fields.includes(key))) {
    return null;
  }
  if (kind === "ping") {
    return { v: MAIL_REQUEST_VERSION, id, kind };
  }
  if (!isMailRecipient(to)) {
    return null;
  }
  if (kind === "changed") {
    return { v: MAIL_REQUEST_VERSION, id, kind, to };
  }
  return isMailToken(token) ? { v: MAIL_REQUEST_VERSION, id, kind, to, token } : null;
}

/**
 * Writes a request as the body the gateway signs and posts: its fields alone, in one order.
 *
 * @param request The request.
 * @returns The JSON text.
 * @throws RangeError when the reader would refuse the request, so nothing is signed that the Worker would not
 *   take.
 */
export function mailRequestBody(request: MailRequest): string {
  const fields: Record<string, unknown> = { v: request.v, id: request.id, kind: request.kind };
  if (request.kind !== "ping") {
    fields["to"] = request.to;
  }
  if (request.kind === "verify" || request.kind === "reset") {
    fields["token"] = request.token;
  }
  if (readMailRequest(fields) === null) {
    throw new RangeError("a mail request must be one the reader accepts");
  }
  return JSON.stringify(fields);
}
